import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Loader2, Plus, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CustomerAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { CUSTOMER_TYPES } from "@/lib/india";

export default function CustomersPage() {
    const { permissions } = useAuth();
    const canCreate = permissions.includes("customer:create");
    const [q, setQ] = useState("");
    const [type, setType] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [density, setDensity] = useState("comfortable");

    async function load() {
        setLoading(true);
        try {
            const params = { page, page_size: 25 };
            if (q) params.q = q;
            if (type) params.customer_type = type;
            setData(await CustomerAPI.list(params));
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, type]);

    function onSearchKey(e) {
        if (e.key === "Enter") {
            setPage(1);
            load();
        }
    }

    const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / 25)), [data.total]);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="customers-page">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Customers</h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Manage individual customers, businesses with GSTIN, and fleets.
                    </p>
                </div>
                {canCreate && (
                    <Link to="/customers/new">
                        <Button data-testid="customer-create-btn" className="bg-brand-red text-white hover:bg-brand-redDeep">
                            <Plus className="mr-1 h-4 w-4" /> New customer
                        </Button>
                    </Link>
                )}
            </header>

            <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 p-3 dark:border-zinc-800">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                        <Input
                            placeholder="Search name, phone, code, registration…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={onSearchKey}
                            className="w-72 pl-8"
                            data-testid="customers-search"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={type}
                            onChange={(e) => { setType(e.target.value); setPage(1); }}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="customers-type"
                        >
                            <option value="">All types</option>
                            {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <Button
                            variant={density === "compact" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setDensity("compact")}
                            data-testid="density-compact"
                            className={density === "compact" ? "bg-brand-red text-white" : ""}
                        >Compact</Button>
                        <Button
                            variant={density === "comfortable" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setDensity("comfortable")}
                            data-testid="density-comfortable"
                            className={density === "comfortable" ? "bg-brand-red text-white" : ""}
                        >Comfortable</Button>
                    </div>
                </div>

                {!loading && data.items.length === 0 ? (
                    <div className="p-6">
                        <EmptyState
                            icon={Briefcase}
                            title="No customers yet"
                            description="Add your first customer to start tracking vehicles, services and invoices."
                            action={canCreate && <Link to="/customers/new"><Button className="bg-brand-red text-white hover:bg-brand-redDeep">Add customer</Button></Link>}
                        />
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Code</TableHead>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Name</TableHead>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Phone</TableHead>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Type</TableHead>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Vehicles</TableHead>
                                <TableHead className="font-mono text-[10px] uppercase tracking-wider">Tags</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-zinc-500">
                                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
                                </TableCell></TableRow>
                            )}
                            {data.items.map((c) => (
                                <TableRow
                                    key={c.id}
                                    data-testid={`customer-row-${c.customer_code}`}
                                    className={density === "compact" ? "[&>td]:py-2" : "[&>td]:py-3"}
                                >
                                    <TableCell className="font-mono text-xs text-brand-redDeep dark:text-brand-red">
                                        <Link to={`/customers/${c.id}`} className="hover:underline">{c.customer_code}</Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/customers/${c.id}`} className="font-medium hover:underline">{c.display_name}</Link>
                                        {c.gstin && <div className="font-mono text-[10px] text-zinc-500">GSTIN {c.gstin}</div>}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">{c.phone}</TableCell>
                                    <TableCell>
                                        <span className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                                            {c.customer_type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="font-mono tabular-nums">{c.vehicle_count ?? 0}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {(c.tags || []).slice(0, 4).map((t) => (
                                                <span key={t} className="rounded bg-brand-redSoft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red">{t}</span>
                                            ))}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2 text-xs text-zinc-500 dark:border-zinc-800">
                    <div>{data.total} total · page {page} / {totalPages}</div>
                    <div className="flex gap-1">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
